import { Helmet } from "react-helmet";
import { memo, useContext, useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useLocation } from "react-router-dom";
import { MixpanelContext } from "../../context/AnalyticsService";
import StandardBorderedContainer from "../../components/Containers/StandardBorderedContainer";
import { Colors } from "../../utils/colors";
import CustomTextField from "../../components/CustomTextField";
import { createExternalEventLink, makeEmptyEvent } from "../../utils/makeEvent";
import RectangleButton from "../../components/Buttons/RectangleButton";
import ConfirmExternalLink from "../../components/CreateEvent/ExternalLinks/ConfirmExternalLink";
import { Event } from "@markit/common.types";
import { useNavigate } from "../../hooks/useNavigate";
import PaymentFailedButtonModal from "../../components/DisplayModal/UpdatePaymentInfoModal";
import { useOnMount } from "../../hooks/useOnMount";
import {
  isSubscriptionPaymentFailed,
  normalizeUrlWithHttps,
} from "@markit/common.utils";
import CreatorModeWrapper from "../../components/CreatorDashboard/CreatorModeWrapper";
import { NewProductNavigationId } from "../../navigation/AppParamList";

const CreateExternalLink = () => {
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const { confirmState, event } = useLocation().state || {};
  const [externalLink, setExternalLink] = useState("");
  const [error, setError] = useState(false);
  const [showLinkStatus, setShowLinkStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmLinkScreen, setConfirmLinkScreen] = useState(
    confirmState ?? false
  );
  const [paymentFailedVisible, setPaymentFailedVisible] = useState(false);

  // Check if the subscription is Payment Failed
  useOnMount(() => {
    if (isSubscriptionPaymentFailed(accountData.customer.state)) {
      setPaymentFailedVisible(true);
    }
  });

  const [externalEvent, setExternalEvent] = useState<Event>(
    event ?? makeEmptyEvent(accountData.uid, accountData.isAdmin)
  );

  const isValidExternalUrl = useCallback((url: string) => {
    try {
      const trimmedUrl = url.trim();
      if (!trimmedUrl.toLowerCase().startsWith("https://")) {
        return false;
      }
      const parsedUrl = new URL(trimmedUrl);
      return parsedUrl.protocol === "https:";
    } catch {
      return false;
    }
  }, []);

  const styles = {
    subtext: { fontSize: 14, color: Colors.GRAY1 },
    standardContainer: {
      backgroundColor: Colors.WHITE,
      padding: "24px 14px 14px 14px",
      width: 390,
    },
  };

  const onChangeText = useCallback(
    (e: string) => {
      if (error) {
        setError(false);
      }
      if (isValidExternalUrl(e)) {
        setShowLinkStatus(true);
      } else {
        setShowLinkStatus(false);
      }
      setExternalLink(e);
    },
    [error, isValidExternalUrl]
  );

  const createLinkOnPress = useCallback(async () => {
    if (!isValidExternalUrl(externalLink)) {
      setShowLinkStatus(true);
      setError(true);
      return;
    }
    const normalizedExternalLink = normalizeUrlWithHttps(externalLink);
    setLoading(true);
    const { event: externalEventData, dataScraped } =
      await createExternalEventLink(normalizedExternalLink, accountData);
    if (!externalEventData) {
      setLoading(false);
      setError(true);
      return;
    }

    if (mixpanel) {
      mixpanel.track("Generate External Event Link", {
        event_id: externalEventData.id,
        external_link: externalEventData.externalLink,
        type: externalEventData.eventType,
        scraped_data: dataScraped,
        platform: "webapp",
      });
    }
    if (dataScraped) {
      setExternalEvent(externalEventData);
      setConfirmLinkScreen(true);
    } else {
      navigate(NewProductNavigationId.EVENT, {
        state: {
          duplicatedEvent: externalEventData,
          externalNotScraped: true,
        },
      });
    }

    setLoading(false);
  }, [accountData, externalLink, isValidExternalUrl, mixpanel, navigate]);

  const backOnPress = useCallback(() => {
    setConfirmLinkScreen(false);
    setExternalEvent(
      event ?? makeEmptyEvent(accountData.uid, accountData.isAdmin)
    );
    setExternalLink("");
    setShowLinkStatus(false);
  }, [accountData.isAdmin, accountData.uid, event]);

  return (
    <div>
      <Helmet>
        <title>{"Create External Link"}</title>
        <meta name="og:title" content={"Create External Link"} />
        <meta name="og:description" content={"Create External Link"} />
      </Helmet>
      <CreatorModeWrapper>
        <div className="Centering" style={{ paddingBlock: 100 }}>
          {confirmLinkScreen ? (
            <ConfirmExternalLink
              event={externalEvent}
              backOnPress={backOnPress}
            />
          ) : (
            <StandardBorderedContainer
              containerStyles={{
                ...styles.standardContainer,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  backgroundColor: Colors.BLACK1,
                  height: 47,
                  width: 47,
                  borderRadius: 100,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: 24,
                }}
              >
                <Icon
                  icon={"mdi:link-variant"}
                  style={{ color: Colors.WHITE }}
                  height={34}
                />
              </div>
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span style={{ fontWeight: "600", fontSize: 17 }}>
                  Link an Event
                </span>
                <span style={styles.subtext}>
                  Create a unique link that will track open analytics and
                  redirect users to your linked event.
                </span>
              </div>
              <div style={{ paddingBlock: 30 }}>
                <CustomTextField
                  placeholder="https://..."
                  inputMode="url"
                  value={externalLink}
                  onChange={(change: any) => onChangeText(change.target.value)}
                  error={error}
                  backgroundColor={Colors.GRAY6}
                />
                {showLinkStatus ? (
                  <div className="AlignedRow" style={{ paddingTop: 7, gap: 4 }}>
                    <Icon
                      icon={"ion:checkmark-circle"}
                      height={14}
                      style={{ color: error ? Colors.RED2 : Colors.GREEN2 }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        color: error ? Colors.RED2 : Colors.GREEN2,
                      }}
                    >
                      {error
                        ? "Invalid URL (must start with https://)"
                        : "Valid URL"}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="ColumnNormal" style={{ gap: 10 }}>
                <span style={{ ...styles.subtext, fontWeight: "500" }}>
                  Example URLs:
                </span>
                <span style={styles.subtext}>Eventbrite</span>
                <span style={styles.subtext}>Luma</span>
                <span style={styles.subtext}>Partiful</span>
                <span style={styles.subtext}>Posh</span>
                <span style={styles.subtext}>Markit</span>
              </div>
              <RectangleButton
                buttonLabel={"Link Event"}
                onPress={createLinkOnPress}
                disabled={externalLink === ""}
                loading={loading}
                containerStyles={{ marginTop: 100 }}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
              />
            </StandardBorderedContainer>
          )}
        </div>
      </CreatorModeWrapper>
      {paymentFailedVisible ? (
        <PaymentFailedButtonModal
          location="Create Event"
          isVisible={paymentFailedVisible}
          setIsVisible={() => {
            setPaymentFailedVisible(false);
          }}
          addOnClose={() => navigate("/home")}
        />
      ) : null}
    </div>
  );
};

export default memo(CreateExternalLink);
